import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { CostCenterDefinition, CostCenterType } from '../services/types';

export interface CostCenterEditorDialogProps {
  /** null = create mode, non-null = edit mode */
  costCenter: CostCenterDefinition | null;
  open: boolean;
  saving?: boolean;
  /** Called with the validated field values. Caller handles the API call. */
  onSubmit: (values: CostCenterFormValues) => void;
  onClose: () => void;
}

export interface CostCenterFormValues {
  code: string;
  displayName: string;
  type: CostCenterType;
  ownerEmail?: string;
  budgetMonthlyEur?: number;
  validFrom?: string;
  validTo?: string | null;
  active: boolean;
}

function isValidBudget(raw: string): boolean {
  if (raw.trim() === '') return false;
  const n = Number(raw);
  if (isNaN(n)) return false;
  if (n < 0) return false;
  return true;
}

export function CostCenterEditorDialog({
  costCenter,
  open,
  saving = false,
  onSubmit,
  onClose,
}: CostCenterEditorDialogProps) {
  const isEdit = costCenter !== null;

  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [type, setType] = useState<CostCenterType>('fachbereich');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [budgetRaw, setBudgetRaw] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [active, setActive] = useState(true);

  // Reset form whenever the dialog opens or the cost-center changes
  useEffect(() => {
    if (open) {
      setCode(costCenter?.code ?? '');
      setDisplayName(costCenter?.displayName ?? '');
      setType(costCenter?.type ?? 'fachbereich');
      setOwnerEmail(costCenter?.ownerEmail ?? '');
      setBudgetRaw(
        costCenter?.budgetMonthlyEur !== undefined && costCenter?.budgetMonthlyEur !== null
          ? String(costCenter.budgetMonthlyEur)
          : ''
      );
      setValidFrom(costCenter?.validFrom ?? '');
      setValidTo(costCenter?.validTo ?? '');
      setActive(costCenter !== null ? costCenter.active : true);
    }
  }, [open, costCenter]);

  const codeTrim = code.trim();
  const nameTrim = displayName.trim();
  const budgetRequired = type === 'fachbereich';
  // NC-195 finding (2026-06-22): validity must not depend on the TYPE, only on
  // whether a value was entered. The old `!budgetRequired || isValid(...)` made
  // `budgetValid` unconditionally true for type=plattform, so a negative budget
  // enabled Submit and only failed on the BE's `z.number().min(0)` — a 400
  // round-trip instead of inline feedback. Empty stays legal for plattform.
  const budgetValid =
    budgetRaw.trim() === '' ? !budgetRequired : isValidBudget(budgetRaw);
  const codeValid = isEdit || codeTrim.length > 0;
  const formValid = codeValid && nameTrim.length > 0 && budgetValid;

  const handleSubmit = () => {
    if (!formValid) return;
    onSubmit({
      code: codeTrim,
      displayName: nameTrim,
      type,
      ownerEmail: ownerEmail.trim() === '' ? undefined : ownerEmail.trim(),
      budgetMonthlyEur: budgetRaw.trim() === '' ? undefined : Number(budgetRaw),
      validFrom: validFrom.trim() === '' ? undefined : validFrom,
      validTo: validTo.trim() === '' ? null : validTo,
      active,
    });
  };

  const title = isEdit ? 'Kostenstelle bearbeiten' : 'Neue Kostenstelle';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="costcenter-editor-dialog-title"
    >
      <DialogTitle id="costcenter-editor-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Kostenstellen-Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={saving || isEdit}
            helperText={isEdit ? 'Code ist nach Anlage nicht änderbar.' : 'z. B. KST-4711'}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Kostenstellen-Code' }}
            autoFocus={!isEdit}
          />
          <TextField
            label="Anzeigename"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={saving}
            fullWidth
            required
            inputProps={{ 'aria-label': 'Anzeigename' }}
          />
          <TextField
            select
            label="Typ"
            value={type}
            onChange={(e) => setType(e.target.value as CostCenterType)}
            disabled={saving}
            fullWidth
            inputProps={{ 'aria-label': 'Typ' }}
          >
            <MenuItem value="fachbereich">Fachbereich</MenuItem>
            <MenuItem value="plattform">Plattform</MenuItem>
          </TextField>
          <TextField
            label="Monatsbudget (EUR)"
            type="number"
            value={budgetRaw}
            onChange={(e) => setBudgetRaw(e.target.value)}
            disabled={saving}
            fullWidth
            required={budgetRequired}
            helperText={budgetRequired ? 'Pflicht für Fachbereich-Kostenstellen.' : 'Optional für Plattform.'}
            inputProps={{ 'aria-label': 'Monatsbudget (EUR)', min: 0, step: 1 }}
          />
          <TextField
            label="Owner-E-Mail"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            disabled={saving}
            fullWidth
            inputProps={{ 'aria-label': 'Owner-E-Mail' }}
          />
          <TextField
            label="Gültig ab"
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            disabled={saving}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Gültig ab' }}
          />
          <TextField
            label="Gültig bis"
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            disabled={saving}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Gültig bis' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Aktiv"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving || !formValid}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Speichern…' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CostCenterEditorDialog;
